// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { X, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUESTIONS = [
  {
    id: 'obiettivo',
    q: 'Qual è il tuo obiettivo principale?',
    opts: [
      { id: 'basi', label: 'Capire le basi del denaro e del risparmio' },
      { id: 'casa', label: 'Comprare casa nei prossimi anni' },
      { id: 'investire', label: 'Iniziare a investire per il futuro' },
      { id: 'fire', label: 'Raggiungere la libertà finanziaria (FIRE)' },
      { id: 'pensione', label: 'Pianificare la pensione' },
    ],
  },
  {
    id: 'lavoro',
    q: 'Qual è la tua situazione lavorativa?',
    opts: [
      { id: 'studente', label: 'Studente o neolaureato' },
      { id: 'dipendente', label: 'Lavoratore dipendente' },
      { id: 'piva', label: 'Libero professionista / P.IVA' },
      { id: 'imprenditore', label: 'Imprenditore' },
      { id: 'cercalavoro', label: 'In cerca di occupazione' },
    ],
  },
  {
    id: 'risparmi',
    q: 'Hai già dei risparmi?',
    opts: [
      { id: 'zero', label: 'No, parto da zero' },
      { id: 'poco', label: 'Meno di €5.000' },
      { id: 'medio', label: '€5.000 – €20.000' },
      { id: 'molto', label: 'Oltre €20.000' },
    ],
  },
  {
    id: 'debiti',
    q: 'Hai un mutuo o debiti significativi?',
    opts: [
      { id: 'nessuno', label: 'No, nessun debito' },
      { id: 'mutuo', label: 'Solo il mutuo prima casa' },
      { id: 'mutuo_altro', label: 'Mutuo + altri debiti' },
      { id: 'debiti', label: 'Debiti da estinguere prioritariamente' },
    ],
  },
  {
    id: 'eta',
    q: 'Quanti anni hai?',
    opts: [
      { id: 'under25', label: 'Under 25' },
      { id: '25-35', label: '25–35 anni' },
      { id: '35-45', label: '35–45 anni' },
      { id: '45-55', label: '45–55 anni' },
      { id: 'over55', label: 'Over 55' },
    ],
  },
];

const generatePercorso = (answers) => {
  const { obiettivo, lavoro, risparmi, debiti, eta } = answers;
  const lessons = [];
  const calcs = [];
  let obiettivoCifra = '';

  // Lezioni consigliate in base agli obiettivi
  if (obiettivo === 'basi' || !obiettivo) {
    lessons.push({ title: "Cos'è il denaro?", lesson_id: 'lesson_001', reason: 'Punto di partenza fondamentale' });
    lessons.push({ title: "L'arte del risparmio", lesson_id: 'lesson_002', reason: 'Prima regola della salute finanziaria' });
    lessons.push({ title: 'Il conto corrente', lesson_id: 'lesson_003', reason: 'Strumento base per gestire il denaro' });
  }
  if (obiettivo === 'casa') {
    lessons.push({ title: "L'arte del risparmio", lesson_id: 'lesson_002', reason: 'Per accumulare l\'anticipo' });
    calcs.push({ slug: 'mutuo', label: 'Simulatore Mutuo', reason: 'Calcola rata e costi reali' });
    calcs.push({ slug: 'affitto-vs-mutuo', label: 'Affitto vs Mutuo', reason: 'Trova la scelta giusta per te' });
    obiettivoCifra = 'Per un anticipo del 20% su €200.000, devi accumulare €40.000. Con risparmio da €500/mese al 3%/anno ci vogliono circa 6 anni.';
  }
  if (obiettivo === 'investire') {
    lessons.push({ title: 'Introduzione agli investimenti', lesson_id: 'lesson_004', reason: 'Prima di investire, capisci le basi' });
    calcs.push({ slug: 'pac', label: 'PAC mensile', reason: 'Il metodo più efficace per iniziare' });
    calcs.push({ slug: 'simulatore-fire', label: 'Simulatore FIRE', reason: 'Vedi quanto crescerà il tuo patrimonio' });
  }
  if (obiettivo === 'fire') {
    calcs.push({ slug: 'simulatore-fire', label: 'Simulatore FIRE', reason: 'Calcola la tua data di libertà finanziaria' });
    calcs.push({ slug: 'pac', label: 'PAC mensile', reason: 'L\'investimento che ti avvicina al FIRE' });
    obiettivoCifra = 'Per raggiungere la libertà finanziaria con €2.000/mese di spese, hai bisogno di €600.000 (regola del 4%). Investendo €800/mese al 7%/anno, ci vorranno circa 23 anni.';
  }
  if (obiettivo === 'pensione') {
    calcs.push({ slug: 'pensione-inps', label: 'Pensione INPS stimata', reason: 'Scopri cosa ti aspetta' });
    calcs.push({ slug: 'pensione-complementare', label: 'Pensione Complementare', reason: 'Colma il gap' });
    calcs.push({ slug: 'tfr', label: 'Calcolatore TFR', reason: 'Ottimizza il tuo TFR' });
  }

  // Aggiusta per P.IVA
  if (lavoro === 'piva') {
    calcs.unshift({ slug: 'partita-iva', label: 'Calcolatore P.IVA', reason: 'Scopri quanto guadagni davvero' });
  }

  // Fondo emergenza sempre utile se risparmi bassi
  if (risparmi === 'zero' || risparmi === 'poco') {
    calcs.push({ slug: 'fondo-emergenza', label: 'Fondo Emergenza', reason: 'Prima ancora di investire, costruisci una rete di sicurezza' });
    if (!obiettivoCifra) obiettivoCifra = 'Prima degli investimenti, costruisci un fondo emergenza di 3-6 mesi di spese. Con €200/mese risparmiati, in 1 anno raggiungi €2.400 di sicurezza.';
  }

  // Se ha debiti
  if (debiti === 'debiti' || debiti === 'mutuo_altro') {
    calcs.push({ slug: 'carta-revolving', label: 'Costo Reale dei Debiti', reason: 'Capire il costo reale dei tuoi debiti è prioritario' });
  }

  // Budget mensile quasi sempre utile
  if (!calcs.find(c => c.slug === 'budget-mensile')) {
    calcs.splice(0, 0, { slug: 'budget-mensile', label: 'Budget Mensile', reason: 'La base di qualsiasi piano finanziario' });
  }

  // Lezioni base se nessuna aggiunta
  if (lessons.length === 0) {
    lessons.push({ title: "Cos'è il denaro?", lesson_id: 'lesson_001', reason: 'Punto di partenza' });
    lessons.push({ title: "L'arte del risparmio", lesson_id: 'lesson_002', reason: 'Base del risparmio' });
  }

  return {
    lessons: lessons.slice(0, 4),
    calcs: calcs.slice(0, 4),
    obiettivoCifra: obiettivoCifra || 'Completa il Budget Mensile per ricevere un obiettivo numerico personalizzato.',
  };
};

const PercorsoPersonalizzato = ({ user, onClose, onComplete }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleAnswer = async (qId, optId) => {
    const newAnswers = { ...answers, [qId]: optId };
    setAnswers(newAnswers);
    if (step < QUESTIONS.length - 1) {
      setStep(s => s + 1);
    } else {
      // Last question - generate result
      setSaving(true);
      const percorso = generatePercorso(newAnswers);
      setResult(percorso);
      // Save to backend
      try {
        await fetch(`${API_URL}/api/user/percorso`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ answers: newAnswers, percorso }),
        });
      } catch {}
      setSaving(false);
      if (onComplete) onComplete(percorso);
    }
  };

  const q = QUESTIONS[step];
  const progress = result ? 100 : Math.round((step / QUESTIONS.length) * 100);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }} data-testid="percorso-modal">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-lg" style={{ background: '#252523', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div>
            <h2 className="text-base font-semibold text-[#E8E8E4]">Il tuo percorso personalizzato</h2>
            {!result && <p className="text-xs text-[#888780] mt-0.5">Domanda {step + 1} di {QUESTIONS.length}</p>}
          </div>
          <button onClick={onClose} className="text-[#888780] hover:text-[#E8E8E4] transition-colors p-1" data-testid="percorso-close-btn"><X className="w-4 h-4" /></button>
        </div>

        {/* Progress bar */}
        <div style={{ height: 3, background: 'rgba(255,255,255,0.06)' }}>
          <motion.div animate={{ width: `${progress}%` }} style={{ height: '100%', background: '#C9A84C', transition: 'width 0.3s' }} />
        </div>

        <div className="p-6">
          <AnimatePresence mode="wait">
            {!result ? (
              <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <p className="text-lg font-medium text-[#E8E8E4] mb-5">{q.q}</p>
                <div className="space-y-2">
                  {q.opts.map(opt => (
                    <button key={opt.id} onClick={() => handleAnswer(q.id, opt.id)} data-testid={`percorso-opt-${opt.id}`}
                      className="w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all"
                      style={{ background: '#1C1C1A', border: '1px solid rgba(255,255,255,0.08)', color: '#E8E8E4' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(201,168,76,0.5)'; e.currentTarget.style.background = 'rgba(201,168,76,0.05)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.background = '#1C1C1A'; }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🎯</span>
                  <h3 className="text-lg font-semibold text-[#E8E8E4]">Il tuo percorso è pronto!</h3>
                </div>

                {/* Obiettivo numerico */}
                <div className="p-4 rounded-xl mb-4" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)' }}>
                  <p className="text-xs text-[#C9A84C] font-semibold uppercase tracking-wider mb-1">Obiettivo numerico</p>
                  <p className="text-sm text-[#E8E8E4] leading-relaxed">{result.obiettivoCifra}</p>
                </div>

                {/* Lezioni */}
                {result.lessons.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-[#888780] uppercase tracking-wider mb-2">Lezioni consigliate</p>
                    <div className="space-y-2">
                      {result.lessons.map(l => (
                        <button key={l.lesson_id} onClick={() => { navigate(`/lezione/${l.lesson_id}`); onClose(); }}
                          className="w-full text-left p-3 rounded-xl flex items-center justify-between transition-all group"
                          style={{ background: '#1C1C1A', border: '1px solid rgba(255,255,255,0.06)' }}
                          data-testid={`percorso-lesson-${l.lesson_id}`}>
                          <div>
                            <p className="text-sm font-medium text-[#E8E8E4]">{l.title}</p>
                            <p className="text-xs text-[#888780]">{l.reason}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#888780] group-hover:text-[#C9A84C] transition-colors" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Calcolatori */}
                {result.calcs.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-[#888780] uppercase tracking-wider mb-2">Calcolatori prioritari</p>
                    <div className="space-y-2">
                      {result.calcs.map(c => (
                        <button key={c.slug} onClick={() => { navigate(`/calcolatori/${c.slug}`); onClose(); }}
                          className="w-full text-left p-3 rounded-xl flex items-center justify-between transition-all group"
                          style={{ background: '#1C1C1A', border: '1px solid rgba(255,255,255,0.06)' }}
                          data-testid={`percorso-calc-${c.slug}`}>
                          <div>
                            <p className="text-sm font-medium text-[#C9A84C]">{c.label}</p>
                            <p className="text-xs text-[#888780]">{c.reason}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#888780] group-hover:text-[#C9A84C] transition-colors" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <button onClick={onClose} className="w-full sapiens-btn-primary py-2.5 text-sm font-medium" data-testid="percorso-done-btn">
                  Inizia il tuo percorso →
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default PercorsoPersonalizzato;
